import { Col, FormInstance, Row } from "antd"
import React, { Component, ReactNode } from "react"
import KeyboardJs from "react-simple-keyboard"

import { alphaKeyboard, codeKeyboard, docNameKeyboard, emailKeyboard, keyboardLayout, mssEmailKeyboard, nirKeyboard, noLayoutKeyboard, numericKeyboard, passwordKeyboard, textKeyboard, twoFaKeyboard, locationKeyboard } from "styles/keyboard"
import { getCountryCallingCode } from "react-phone-number-input"

import styles from "./Keyboard.module.scss"
import { InputNames } from "types/props"
import { cdn } from "core/cdn"

/*
  Ce clavier JS HOC nécéssite un type Form.Item en props si Form est utilisé
  afin de modifier la valeur
------------------------------------------------
    Par HOC -  déconseillé
    --------------
    options : KeyboardProps.options
    withKeyboard(wrappedComponent, options)
    -> le hoc identifiera le input grâce au options.inputName
    -> Children hérite de : 
    - onChange( value )
    - onClick ( initialValue )
------------------------------------------------
    Par JSXELEMENT 
    --------------
    <Keyboard props : KeyboardProps >
    Si le input ciblé a ses propriétés [onChange, onClick] overwrités, 
    ne pas oublié de faire :
    onChange={props.onChange}
    onClick={props.onClick}
    Sinon le clavier n'atteindra jamais son input cible
*/
const NAMES_MUST_BE_CAPITALIZED = [InputNames.FIRSTNAME, InputNames.LASTNAME, InputNames.FIRST_BIRTH_FIRSTNAME, InputNames.BIRTH_LASTNAME] as Array<string>
export interface KeyboardChildrenProps {
  value: string
  form?: FormInstance
  name?: string
  readOnly?: boolean
  onClick?: () => void
  onChange?: (value: React.ChangeEvent | string) => void
}
interface KeyboardProps {
  event?: any
  options?: {
    nextButton?: string
    title?: string
    inputName?: string
    type?: "nir" | "num" | "text" | "email" | "phone" | string
    unit?: "kg"  | string
    resultsDisplay?: ReactNode
  }
  form?: FormInstance
  readOnly?: boolean
  children?: ReactNode
  value?: string
  onChange?: (s: any) => void
  containerStyle?: React.CSSProperties
}
interface KeyboardState {
  value: string
  visible: boolean
  layout: { default: string[]; shift?: string[] }
  mode: string
}

const keyboards = {
  text: textKeyboard,
  nir: nirKeyboard,
  email: emailKeyboard,
  mss_email: mssEmailKeyboard,
  tel: numericKeyboard,
  num: numericKeyboard,
  password: passwordKeyboard,
  code: codeKeyboard,
  twoFa: twoFaKeyboard,
  alpha: alphaKeyboard,
  docname: docNameKeyboard,
  location: locationKeyboard,
}

const getLayoutByType = (
  type: string
): { default: string[]; shift?: string[] } => {
  switch (type) {
    case "gender":
      return noLayoutKeyboard;
    case "num":
    case "numeric":
    case "finess":
    case "finess_ej":
      return keyboards.code
    case "code":
    case "rpps":
    case "birthdate":
      return keyboards.code
    case "twoFa":
      return keyboards.twoFa
    case "phone":
      return keyboards.tel
    case "password":
    case "new-password":
    case "old-password":
      return keyboards.password
    case "email":
      return keyboards.email
    case "mss_email":
      return keyboards.mss_email
    case "nir":
      return keyboards.nir
    case "alpha":
      return keyboards.alpha
    case "docname":
      return keyboards.docname
    case InputNames.BIRTH_LOCATION:
      return keyboards.location
    default:
    case "text":
      return keyboards.text
  }
}
interface KeyboardResetProps {
  style?: React.CSSProperties
  onClick?: () => void
}
export const KeyboardReset: React.FC<KeyboardResetProps> = (props): JSX.Element => {
  return (
    <div {...props} className={styles.KeyboardReset}>
      <img src={cdn("/icons/error.svg")} alt="close" />
    </div>
  )
}

type KeyboardReference = unknown & {
  replaceInput: any
  setInput: (string: string) => void
}
export class Keyboard extends Component<KeyboardProps, KeyboardState> {
  keyboard: KeyboardReference | undefined = undefined
  constructor(props: KeyboardProps) {
    super(props)
    this.event()

    const type = props.options?.type || "undefined"
    const layout = getLayoutByType(type)
    this.state = {
      value: props.value || "",
      mode: this.mustCapitalized() ? "shift" : "default",
      visible: true,
      layout,
    }
  }

  maxLength: number | undefined
  inputElement: HTMLInputElement | null = null

  setInput(name: string | undefined) {
    if (!name) this.inputElement = null
    const input = document.querySelector(`input[name='${name}']`)
    this.inputElement = input as HTMLInputElement
  }

  setLayout() {
    const type = this.props.options?.type || "undefined"
    const layout = getLayoutByType(type)
    const mode = this.mustCapitalized() ? "shift" : "default"
    if (layout !== this.state.layout)
      this.setState({
        ...this.state,
        layout,
        mode,
      })
  }

  event() {
    this.props.event?.subscribe((event) => {
      switch (event) {
        case "reset":
          this.keyboard.replaceInput({})
          break
        default:
          break
      }
    })
  }

  setMaxLength() {
    const lengthFromMask = this.getMaxLengthOf(this.inputElement)
    if (lengthFromMask) this.maxLength = lengthFromMask
    else
      this.maxLength =
        Number(this.inputElement?.getAttribute("maxlength")) || undefined
  }

  mustCapitalized = () =>
    !this.inputElement?.value?.length &&
    NAMES_MUST_BE_CAPITALIZED.includes(this.props.options?.inputName ?? "")

  linkInput = (input?: string) => {
    this.setInput(input)
    this.setMaxLength()
    this.setLayout()
  }

  componentDidUpdate = (prevProps: Readonly<KeyboardProps>): void => {
    const inputChanged =
    prevProps.options?.inputName !== this.props.options?.inputName
    const valueChanged = prevProps.value !== this.props.value
    if (!this.inputElement || inputChanged) {
      this.linkInput(this.props.options?.inputName)
    }

    
    // Allow modification of injected default value
    if ([`${InputNames.FIRSTNAME}`, `${InputNames.LASTNAME}`].includes(this.props.options.inputName)) {
      this.keyboard?.setInput(this.props.value)
    }
    
    if (valueChanged) {
      this.onUserKeyboardChange({
        [this.props.options?.inputName as string]: this.props.value || "",
      })
      inputChanged && this.keyboard?.replaceInput({})
    }
  }

  onValueChange = (inputs: { [x: string]: string }): void => {
    const value = inputs[this.props.options?.inputName as string]
    const mode = this.mustCapitalized() ? "shift" : "default"
    this.setState({ ...this.state, value, mode })
  }

  onUserKeyboardChange = (inputs: { [x: string]: string }) => {
    this.keyboard?.setInput(inputs[this.props.options?.inputName as string])
    this.onValueChange(inputs)
  }

  // Calling code of the selected country, always present in a phone value
  getPhoneCode = (): string =>
    "+" +
    getCountryCallingCode(
      this.props.form?.getFieldValue("country-phone") || "FR"
    )

  // A phone value made of the calling code alone is not user input
  hasUserInput = (value: string): boolean => {
    if (!value) return false
    if (this.props.options?.type !== "phone") return true
    const code = this.getPhoneCode()
    const rest = value.startsWith(code) ? value.slice(code.length) : value
    return rest.trim().length > 0
  }

  onJSKeyboardChange = (inputs: { [x: string]: string }) => {
    const inputName = this.props.options?.inputName as string
    let value = inputs[inputName] || ""

    if (this.props.options?.type === "phone") {
      const code = this.getPhoneCode()
      if (!value.startsWith(code)) {
        value = /^\d+$/.test(value) ? code + value : code
      }
      inputs = { ...inputs, [inputName]: value }
      this.keyboard?.setInput(value)
    }

    this.props.form &&
      this.props.form.setFieldsValue({
        [inputName]: value,
      })
    this.props.onChange && this.props.onChange(value)
    this.onValueChange(inputs)
  }

  handleAccent = (button: string): void => {
    if (button === "{accent}" || button === "{shift-accent}")
      this.setState({ ...this.state, mode: "accent" })
    else if (button === "{accent-upper}" || button === "{shift-accent-upper}")
      this.setState({ ...this.state, mode: "accent-upper" })
    else if (button === "{char}")
      this.setState({ ...this.state, mode: "default" })
    else if (button === "{char-upper}")
      this.setState({ ...this.state, mode: "shift" })
  }

  handleShift = (): void => {
    const newMode = this.state.mode === "default" ? "shift" : "default"
    this.setState({ ...this.state, mode: newMode })
  }

  onKeyPress = (button: string): void => {
    if (button === "{bksp-digit}") {
      const currentValue = this.state.value ?? ""
      const newValue = currentValue.slice(0, -1)
      this.keyboard?.setInput(newValue)
      this.onJSKeyboardChange({
        [this.props.options?.inputName as string]: newValue,
      })
      return
    }
    if (["{shift}"].includes(button)) {
      this.handleShift()
    }
    else if (
      [
        "{accent}",
        "{char}",
        "{char-upper}",
        "{shift-accent}",
        "{accent-upper}",
        "{shift-accent-upper}",
      ].includes(button)
    )
      this.handleAccent(button)
    else if (this.state.mode !== "default") this.handleShift()
  }

  getMaxLengthOf = (
    inputElement: HTMLInputElement | null
  ): number | undefined => {
    const maxLength =
      !inputElement?.maxLength || inputElement?.maxLength <= 0
        ? 0
        : inputElement.maxLength
    const x = this.removeMaskFromMaxLength(inputElement, maxLength)
    return x
  }

  removeMaskFromMaxLength = (
    input: HTMLInputElement | null,
    maxLength: number
  ): number => {
    const mask = input?.getAttribute("mask")?.split("") || []
    const numberOfSpacerInMask = mask.filter(
      (char) => !["#", "1"].includes(char)
    ).length
    return maxLength - numberOfSpacerInMask
  }

  render = (): JSX.Element => {
    const options = this.props.options
    const { value, layout, mode, visible } = this.state
    const resetable = ![
      "gender",
      "finess",
      InputNames.BIRTHDATE,
      InputNames.TWO_FACTOR_AUTH,
    ].includes(this.props.options?.type as InputNames)
    const keyboardJsProps = {
      layout,
      disableButtonHold: true,
      value,
      layoutName: mode,
      inputName: options?.inputName || "undefined",
      maxLength: this.maxLength,
    }

    const applyGap = layout === keyboards.twoFa || layout === keyboards.code

    const getResetMarginByType = (title: InputNames) => {
      switch (title) {
        case InputNames.PASSWORD:
        case InputNames.OLD_PASSWORD:
          return { marginRight: "80px" }
        case InputNames.NEW_PASSWORD:
          return { marginRight: "80px", marginTop: "-30px" }
        case InputNames.FIRST_BIRTH_FIRSTNAME:
        case InputNames.BIRTH_LASTNAME:
          return { marginTop: "-35px" }
        case InputNames.BIRTH_LOCATION:
          return { marginTop: "-50px", marginRight: "50px" }
        default:
          return {}
      }
    }

    return (
      <div
        className={`${styles.InputContainer} ${applyGap && styles.Gap50}`}
        style={{ width: this.props.options.unit ? '1200px' : 'auto', ...this.props.containerStyle }}
      >
        <Row
          style={{
            width: this.props.options?.unit ? "40%" : "100%",
            marginLeft: "auto",
            marginRight: "auto",
          }}
          justify="space-around"
          align="middle"
          gutter={[20, 20]}
        >
          <Col span={this.props.options?.unit ? 14 : 20}>
            {this.props.children}
            {resetable && this.hasUserInput(value) ? (
              <KeyboardReset
                style={{
                  cursor: "pointer",
                  ...getResetMarginByType(this.props.options?.title as InputNames),
                }}
                onClick={() => {
                  this.keyboard?.setInput("")
                  this.onJSKeyboardChange({
                    [this.props.options?.inputName as string]: "",
                  })
                }}
              />
            ) : null}
          </Col>
          {this.props.options?.unit && (
            <Col span={6}>
              <span className={styles.Unit}>{this.props.options?.unit}</span>
            </Col>
          )}
        </Row>
        {this.props.options?.resultsDisplay && 
          <div style={{ textAlign: "center" }}>{this.props.options?.resultsDisplay}</div>
        }
        {visible && (
          <KeyboardJs
            {...keyboardJsProps}
            {...keyboardLayout}
            keyboardRef={(r: KeyboardReference) => {
              const isInit = !this.keyboard
              this.keyboard = r
              if (isInit && this.props.form && typeof this.props.value === "string" && this.props.value)
                r?.setInput(this.props.value)
            }}
            onKeyPress={this.onKeyPress}
            onChangeAll={this.onJSKeyboardChange}
            disableCaretPositioning={true}
          />
        )}
      </div>
    )
  }
}

const withKeyboard = (WrappedComponent: React.FC): React.FC<KeyboardProps> => {
  return (props: KeyboardProps) => (
    <Keyboard {...props}>
      <WrappedComponent />
    </Keyboard>
  )
}

export default withKeyboard
